var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useContext } from 'react';
import RcColorPicker from '@rc-component/color-picker';
import useLayoutEffect from 'rc-util/lib/hooks/useLayoutEffect';
import Segmented from '../../../segmented';
import { AggregationColor } from '../../color';
import { PanelPickerContext } from '../../context';
import { genAlphaColor, generateColor } from '../../util';
import ColorClear from '../ColorClear';
import ColorInput from '../ColorInput';
import ColorSlider from '../ColorSlider';
import GradientColorBar from './GradientColorBar';
const components = {
    slider: ColorSlider,
};
const PanelPicker = () => {
    const panelPickerContext = useContext(PanelPickerContext);
    const { mode, onModeChange, modeOptions, prefixCls, allowClear, value, disabledAlpha, onChange, onClear, onChangeComplete, activeIndex, gradientDragging } = panelPickerContext, injectProps = __rest(panelPickerContext, ["mode", "onModeChange", "modeOptions", "prefixCls", "allowClear", "value", "disabledAlpha", "onChange", "onClear", "onChangeComplete", "activeIndex", "gradientDragging"]);
    // ============================ Colors ============================
    const colors = React.useMemo(() => {
        if (!value.cleared) {
            return value.getColors();
        }
        return [
            {
                percent: 0,
                color: new AggregationColor(''),
            },
            {
                percent: 100,
                color: new AggregationColor(''),
            },
        ];
    }, [value]);
    // ========================= Single Color =========================
    const isSingle = !value.isGradient();
    // We cache the point color in case user drag the gradient point across another one
    const [lockedColor, setLockedColor] = React.useState(value);
    // Use layout effect here since `useEffect` will cause a blink when mouseDown
    useLayoutEffect(() => {
        var _a;
        if (!isSingle) {
            setLockedColor((_a = colors[activeIndex]) === null || _a === void 0 ? void 0 : _a.color);
        }
    }, [gradientDragging, activeIndex]);
    const activeColor = React.useMemo(() => {
        var _a;
        if (isSingle) {
            return value;
        }
        // Use cache when dragging. User can not operation panel when dragging.
        if (gradientDragging) {
            return lockedColor;
        }
        return (_a = colors[activeIndex]) === null || _a === void 0 ? void 0 : _a.color;
    }, [value, activeIndex, isSingle, lockedColor, gradientDragging]);
    // ============================ Change ============================
    const fillColor = (nextColor) => {
        if (mode === 'single') {
            return nextColor;
        }
        const nextColors = [...colors];
        nextColors[activeIndex] = Object.assign(Object.assign({}, nextColors[activeIndex]), { color: nextColor });
        return new AggregationColor(nextColors);
    };
    const onInternalChange = (colorValue, fromPicker, info) => {
        const nextColor = generateColor(colorValue);
        let submitColor = nextColor;
        if (value.cleared) {
            const rgb = submitColor.toRgb();
            // Auto fill color if origin is `0/0/0` to enhance user experience
            if (!rgb.r && !rgb.g && !rgb.b && info) {
                const { type: infoType, value: infoValue = 0 } = info;
                submitColor = new AggregationColor({
                    h: infoType === 'hue' ? infoValue : 0,
                    s: 1,
                    b: 1,
                    a: infoType === 'alpha' ? infoValue / 100 : 1,
                });
            }
            else {
                submitColor = genAlphaColor(submitColor);
            }
        }
        onChange(fillColor(submitColor), fromPicker);
    };
    const onInternalChangeComplete = (nextColor) => {
        onChangeComplete(fillColor(nextColor));
    };
    // ============================ Render ============================
    // Operation bar
    let operationNode = null;
    const showMode = modeOptions.length > 1;
    if (allowClear || showMode) {
        operationNode = (React.createElement("div", { className: `${prefixCls}-operation` },
            showMode && (React.createElement(Segmented, { size: "small", options: modeOptions, value: mode, onChange: onModeChange })),
            React.createElement(ColorClear, Object.assign({ prefixCls: prefixCls, value: value, onChange: (clearColor) => {
                    onChange(clearColor);
                    onClear === null || onClear === void 0 ? void 0 : onClear();
                } }, injectProps))));
    }
    // Return
    return (React.createElement(React.Fragment, null,
        operationNode,
        React.createElement(GradientColorBar, Object.assign({}, panelPickerContext, { colors: colors })),
        React.createElement(RcColorPicker, { prefixCls: prefixCls, value: activeColor === null || activeColor === void 0 ? void 0 : activeColor.toHsb(), disabledAlpha: disabledAlpha, onChange: (colorValue, info) => {
                onInternalChange(colorValue, true, info);
            }, onChangeComplete: (colorValue) => {
                onInternalChangeComplete(generateColor(colorValue));
            }, components: components }),
        React.createElement(ColorInput, Object.assign({ value: activeColor, onChange: onInternalChange, prefixCls: prefixCls, disabledAlpha: disabledAlpha }, injectProps))));
};
export default PanelPicker;
